import React, { useEffect, useMemo, useState } from "react";
import { api, apiErr } from "@/lib/api";
import PortalLayout from "@/components/PortalLayout";
import { toast } from "sonner";
import { Loader2, Clock, ChevronRight, Search, X } from "lucide-react";
import { yearOptions as yearOpts } from "@/lib/years";

const WEEKDAY_RO = ["D", "L", "Ma", "Mi", "J", "V", "S"];
const MONTHS_RO = ["Ianuarie", "Februarie", "Martie", "Aprilie", "Mai", "Iunie", "Iulie", "August", "Septembrie", "Octombrie", "Noiembrie", "Decembrie"];

function fmtH(h) {
  if (!h) return "";
  return Number.isInteger(h) ? String(h) : h.toFixed(1);
}

export default function ViewTimesheets() {
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [employeeId, setEmployeeId] = useState("");
  const [empQuery, setEmpQuery] = useState("");
  const [empOpen, setEmpOpen] = useState(false);
  const [projectId, setProjectId] = useState("");
  const [entries, setEntries] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(() => new Set());

  const toggleExpand = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/staff/view/directory");
        setEmployees(data.employees || []);
        setProjects(data.projects || []);
      } catch (e) { toast.error(apiErr(e)); }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ month });
        if (employeeId) params.set("employee_id", employeeId);
        if (projectId) params.set("project_id", projectId);
        const { data } = await api.get(`/staff/timesheet?${params.toString()}`);
        setEntries(data || []);
      } catch (e) { toast.error(apiErr(e)); }
      finally { setLoading(false); }
    })();
  }, [month, employeeId, projectId]);

  // Concedii aprobate pentru luna selectată (pentru marcaj „CO")
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/timeoff/calendar?month=${month}`);
        setLeaves(data || []);
      } catch { setLeaves([]); }
    })();
  }, [month]);

  const [y, m] = month.split("-").map(Number);
  const yearOptions = useMemo(() => yearOpts(y), [y]);
  const daysInMonth = new Date(y, m, 0).getDate();
  const days = useMemo(() => Array.from({ length: daysInMonth }, (_, i) => i + 1), [daysInMonth]);
  const weekendDay = (d) => {
    const wd = new Date(y, m - 1, d).getDay();
    return wd === 0 || wd === 6;
  };

  const empMatches = useMemo(() => {
    const q = empQuery.trim().toLowerCase();
    const list = q ? employees.filter(u => u.name.toLowerCase().includes(q)) : employees;
    return list.slice(0, 8);
  }, [employees, empQuery]);

  // Aggregate: rows per employee, cell = sum hours per day (+ project breakdown for tooltip)
  const { rows, dayTotals, grandTotal } = useMemo(() => {
    const byEmp = {};
    const dayTot = {};
    let grand = 0;
    for (const e of entries) {
      const day = Number(e.date.slice(8, 10));
      const empId = e.employee_id;
      if (!byEmp[empId]) {
        byEmp[empId] = {
          id: empId,
          name: e.employee?.name || "—",
          job_title: e.employee?.job_title || "",
          cells: {}, // day -> { hours, parts: [] }
          projects: {}, // project_id -> { code, name, hours }
          total: 0,
        };
      }
      const cell = byEmp[empId].cells[day] || { hours: 0, parts: [] };
      cell.hours += e.hours || 0;
      const interval = (e.start_time || e.end_time) ? ` (${e.start_time || "…"}–${e.end_time || "…"})` : "";
      cell.parts.push(`${e.project?.code || "?"}: ${fmtH(e.hours)}h${interval}`);
      byEmp[empId].cells[day] = cell;
      const pid = e.project_id || e.project?.id || "?";
      const pr = byEmp[empId].projects[pid] || { code: e.project?.code || "?", name: e.project?.name || "", hours: 0, days: {} };
      pr.hours += e.hours || 0;
      pr.days[day] = (pr.days[day] || 0) + (e.hours || 0);
      byEmp[empId].projects[pid] = pr;
      byEmp[empId].total += e.hours || 0;
      dayTot[day] = (dayTot[day] || 0) + (e.hours || 0);
      grand += e.hours || 0;
    }
    const list = Object.values(byEmp).sort((a, b) => a.name.localeCompare(b.name));
    return { rows: list, dayTotals: dayTot, grandTotal: grand };
  }, [entries]);

  // Hartă concedii: empId -> { zi: cod } (fără weekend), + meta angajat
  const leaveInfo = useMemo(() => {
    const map = {};
    const meta = {};
    const monthStart = new Date(y, m - 1, 1);
    const monthEnd = new Date(y, m, 0);
    for (const lv of leaves) {
      const empId = lv.user_id;
      if (!empId) continue;
      if (employeeId && empId !== employeeId) continue;
      meta[empId] = { name: lv.employee?.name || "—", job_title: lv.employee?.job_title || "" };
      const code = (lv.type?.code || "CO").toUpperCase().slice(0, 3) || "CO";
      const name = lv.type?.name || "Concediu";
      let cur = new Date((lv.start_date || "") + "T00:00:00");
      const end = new Date((lv.end_date || "") + "T00:00:00");
      if (isNaN(cur) || isNaN(end)) continue;
      if (cur < monthStart) cur = new Date(monthStart);
      while (cur <= end && cur <= monthEnd) {
        const wd = cur.getDay();
        if (wd !== 0 && wd !== 6) {
          const day = cur.getDate();
          if (!map[empId]) map[empId] = {};
          map[empId][day] = { code, name };
        }
        cur.setDate(cur.getDate() + 1);
      }
    }
    return { map, meta };
  }, [leaves, employeeId, y, m]);

  // Rânduri afișate: pontaje + angajați aflați DOAR în concediu (fără pontaj)
  const displayRows = useMemo(() => {
    const existing = new Set(rows.map(r => r.id));
    const extra = [];
    if (!projectId) {
      for (const empId of Object.keys(leaveInfo.map)) {
        if (!existing.has(empId)) {
          extra.push({ id: empId, name: leaveInfo.meta[empId]?.name || "—",
            job_title: leaveInfo.meta[empId]?.job_title || "", cells: {}, total: 0 });
        }
      }
    }
    return [...rows, ...extra].sort((a, b) => a.name.localeCompare(b.name));
  }, [rows, leaveInfo, projectId]);

  return (
    <PortalLayout variant="staff">
      <div className="max-w-[1400px]">
        <div className="mb-8">
          <p className="font-mono text-[11px] uppercase tracking-widest text-brand mb-2">Vizualizare</p>
          <h1 className="font-display font-semibold text-[38px] tracking-[-0.02em] leading-tight text-ink mb-2">Pontaje — toți angajații</h1>
          <p className="text-ink-muted text-sm">Ore pontate pe fiecare zi a lunii. Zilele de concediu aprobat sunt marcate cu o bară diagonală „CO" (nu e necesară pontarea).</p>
        </div>

        <div className="flex flex-wrap items-end gap-3 mb-6">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-ink-muted block mb-1">Luna</label>
            <div className="flex items-center gap-2">
              <select
                value={m}
                onChange={e => setMonth(`${y}-${String(Number(e.target.value)).padStart(2, "0")}`)}
                data-testid="ts-month-select"
                className="border border-neutral-300 px-3 py-2 text-sm bg-white min-w-[130px]">
                {MONTHS_RO.map((name, i) => (
                  <option key={i} value={i + 1}>{name}</option>
                ))}
              </select>
              <select
                value={y}
                onChange={e => setMonth(`${e.target.value}-${String(m).padStart(2, "0")}`)}
                data-testid="ts-year-select"
                className="border border-neutral-300 px-3 py-2 text-sm bg-white">
                {yearOptions.map(yr => (
                  <option key={yr} value={yr}>{yr}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-ink-muted block mb-1">Angajat</label>
            <div className="relative">
              <div className="flex items-center border border-neutral-300 bg-white pl-2 min-w-[220px]">
                <Search className="h-3.5 w-3.5 text-ink-muted shrink-0" />
                <input
                  value={empQuery}
                  onChange={e => { setEmpQuery(e.target.value); setEmpOpen(true); if (e.target.value === "") setEmployeeId(""); }}
                  onFocus={() => setEmpOpen(true)}
                  onBlur={() => setTimeout(() => setEmpOpen(false), 150)}
                  placeholder="Caută angajat…"
                  data-testid="ts-employee-search"
                  className="px-2 py-2 text-sm bg-white outline-none w-full" />
                {(empQuery || employeeId) && (
                  <button type="button" data-testid="ts-employee-clear"
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => { setEmpQuery(""); setEmployeeId(""); setEmpOpen(false); }}
                    className="px-2 text-ink-muted hover:text-ink shrink-0">
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              {empOpen && empMatches.length > 0 && (
                <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-neutral-200 shadow-lg max-h-64 overflow-auto"
                  data-testid="ts-employee-list">
                  {empMatches.map(u => (
                    <button key={u.id} type="button" data-testid={`ts-employee-opt-${u.id}`}
                      onMouseDown={e => e.preventDefault()}
                      onClick={() => { setEmployeeId(u.id); setEmpQuery(u.name); setEmpOpen(false); }}
                      className={`block w-full text-left px-3 py-2 text-sm hover:bg-neutral-50 ${employeeId === u.id ? "bg-brand/10 text-brand" : "text-ink"}`}>
                      {u.name}{u.job_title ? <span className="text-ink-muted"> — {u.job_title}</span> : null}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="w-full sm:w-auto">
            <label className="font-mono text-[10px] uppercase tracking-widest text-ink-muted block mb-1">Proiect</label>
            <select value={projectId} onChange={e => setProjectId(e.target.value)}
              data-testid="ts-project" className="border border-neutral-300 px-3 py-2 text-sm bg-white w-full sm:w-auto sm:min-w-[240px]">
              <option value="">Toate proiectele</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.code} — {p.name}</option>)}
            </select>
          </div>
          <div className="ml-auto flex items-center gap-2 px-4 py-2 bg-brand/[0.06] border border-brand/20 rounded-lg">
            <Clock className="h-4 w-4 text-brand" />
            <span className="text-sm text-ink-muted">Total ore:</span>
            <span data-testid="ts-total" className="font-display font-bold text-lg text-brand">{grandTotal.toFixed(1)} h</span>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-ink-muted">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Se încarcă...
          </div>
        ) : displayRows.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-neutral-200 rounded-xl text-ink-muted" data-testid="ts-empty">
            Nu există pontaje pentru filtrele selectate.
          </div>
        ) : (
          <div className="bg-white border border-neutral-200 overflow-x-auto" data-testid="ts-grid">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-neutral-50">
                  <th className="sticky left-0 z-10 bg-neutral-50 text-left px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-ink-muted border-b border-neutral-200 min-w-[200px]">
                    Angajat
                  </th>
                  {days.map(d => (
                    <th key={d} className={`px-1 py-1.5 text-center font-mono text-[11px] border-b border-neutral-200 w-9 ${weekendDay(d) ? "bg-neutral-100 text-ink-muted" : "text-ink"}`}>
                      <div>{d}</div>
                      <div className="text-[9px] text-ink-muted">{WEEKDAY_RO[new Date(y, m - 1, d).getDay()]}</div>
                    </th>
                  ))}
                  <th className="px-3 py-2 text-right font-mono text-[10px] uppercase tracking-widest text-brand border-b border-neutral-200 bg-neutral-50">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {displayRows.map((r) => {
                  const projList = Object.values(r.projects || {}).sort((a, b) => b.hours - a.hours);
                  const canExpand = projList.length > 0;
                  const isOpen = expanded.has(r.id);
                  return (
                  <React.Fragment key={r.id}>
                  <tr data-testid={`ts-emp-${r.id}`} className="hover:bg-ink-soft/40">
                    <td className="sticky left-0 z-10 bg-white px-3 py-2 border-b border-neutral-100">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => canExpand && toggleExpand(r.id)}
                          disabled={!canExpand}
                          data-testid={`ts-expand-${r.id}`}
                          title={canExpand ? "Vezi proiectele cu ore pontate" : "Fără pontaje"}
                          className={`shrink-0 h-6 w-6 flex items-center justify-center rounded transition-colors ${canExpand ? "text-ink-muted hover:text-brand hover:bg-brand/10" : "text-neutral-200 cursor-default"}`}>
                          <ChevronRight className={`h-4 w-4 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                        </button>
                        <div className="min-w-0">
                          <div className="font-medium text-ink truncate">{r.name}</div>
                          <div className="text-[11px] text-ink-muted truncate">{r.job_title}</div>
                        </div>
                      </div>
                    </td>
                    {days.map(d => {
                      const cell = r.cells[d];
                      const leave = !cell ? leaveInfo.map[r.id]?.[d] : null;
                      if (leave) {
                        return (
                          <td key={d} title={leave.name}
                            data-testid={`ts-leave-${r.id}-${d}`}
                            className={`relative p-0 border-b border-neutral-100 h-[42px] ${weekendDay(d) ? "bg-neutral-50/60" : ""}`}>
                            <span className="pointer-events-none absolute inset-0"
                              style={{ backgroundImage: "linear-gradient(to top right, transparent calc(50% - 0.8px), #d9a441 calc(50% - 0.8px), #d9a441 calc(50% + 0.8px), transparent calc(50% + 0.8px))" }} />
                            <span className="relative z-10 flex items-center justify-center h-full text-[10px] font-bold text-amber-700">CO</span>
                          </td>
                        );
                      }
                      return (
                        <td key={d}
                          title={cell ? cell.parts.join("\n") : ""}
                          className={`px-1 py-2 text-center border-b border-neutral-100 tabular-nums ${weekendDay(d) ? "bg-neutral-50/60" : ""} ${cell ? "font-semibold text-ink" : "text-neutral-300"}`}>
                          {cell ? fmtH(cell.hours) : "·"}
                        </td>
                      );
                    })}
                    <td className="px-3 py-2 text-right font-display font-bold text-brand border-b border-neutral-100 tabular-nums">
                      {fmtH(r.total)}
                    </td>
                  </tr>
                  {isOpen && canExpand && (() => {
                    const activeDays = [...new Set(projList.flatMap(p => Object.keys(p.days || {}).map(Number)))].sort((a, b) => a - b);
                    return (
                    <tr data-testid={`ts-projects-${r.id}`} className="bg-brand/[0.03]">
                      <td colSpan={days.length + 2} className="px-3 py-3 border-b border-neutral-100">
                        <div className="pl-8">
                          <div className="font-mono text-[10px] uppercase tracking-widest text-ink-muted mb-2">
                            Alocare ore pe zile și proiecte — {MONTHS_RO[m - 1]} {y}
                          </div>
                          <div className="overflow-x-auto border border-neutral-200 rounded-lg bg-white inline-block max-w-full">
                            <table className="border-collapse text-[12px]">
                              <thead>
                                <tr className="bg-neutral-50">
                                  <th className="sticky left-0 z-10 bg-neutral-50 text-left px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-ink-muted border-b border-r border-neutral-200 min-w-[160px]">
                                    Proiect
                                  </th>
                                  {activeDays.map(d => (
                                    <th key={d} className={`px-2 py-1.5 text-center font-mono text-[11px] border-b border-neutral-200 w-10 ${weekendDay(d) ? "bg-neutral-100 text-ink-muted" : "text-ink"}`}>
                                      <div>{d}</div>
                                      <div className="text-[9px] text-ink-muted">{WEEKDAY_RO[new Date(y, m - 1, d).getDay()]}</div>
                                    </th>
                                  ))}
                                  <th className="px-3 py-1.5 text-right font-mono text-[10px] uppercase tracking-widest text-brand border-b border-l border-neutral-200 bg-neutral-50">
                                    Total
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {projList.map((pr, i) => (
                                  <tr key={i} data-testid={`ts-matrix-row-${r.id}-${i}`}>
                                    <td className="sticky left-0 z-10 bg-white px-3 py-1.5 border-b border-r border-neutral-100">
                                      <span className="font-semibold text-ink">{pr.code}</span>
                                      {pr.name && <span className="text-ink-muted"> — {pr.name}</span>}
                                    </td>
                                    {activeDays.map(d => (
                                      <td key={d} className={`px-2 py-1.5 text-center tabular-nums border-b border-neutral-100 ${weekendDay(d) ? "bg-neutral-50/60" : ""} ${pr.days[d] ? "font-semibold text-ink" : "text-neutral-300"}`}>
                                        {pr.days[d] ? fmtH(pr.days[d]) : "·"}
                                      </td>
                                    ))}
                                    <td className="px-3 py-1.5 text-right font-display font-bold text-brand border-b border-l border-neutral-100 tabular-nums">
                                      {fmtH(pr.hours)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot>
                                <tr className="bg-neutral-50">
                                  <td className="sticky left-0 z-10 bg-neutral-50 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-ink-muted border-r border-neutral-200">
                                    Total / zi
                                  </td>
                                  {activeDays.map(d => {
                                    const dTot = projList.reduce((s, p) => s + (p.days[d] || 0), 0);
                                    return (
                                      <td key={d} className={`px-2 py-1.5 text-center font-mono text-[11px] tabular-nums ${weekendDay(d) ? "bg-neutral-100" : ""} ${dTot ? "text-ink font-semibold" : "text-neutral-300"}`}>
                                        {dTot ? fmtH(dTot) : "·"}
                                      </td>
                                    );
                                  })}
                                  <td className="px-3 py-1.5 text-right font-display font-bold text-brand border-l border-neutral-200 tabular-nums">
                                    {fmtH(r.total)}
                                  </td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        </div>
                      </td>
                    </tr>
                    );
                  })()}
                  </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-neutral-50">
                  <td className="sticky left-0 z-10 bg-neutral-50 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-ink-muted">
                    Total / zi
                  </td>
                  {days.map(d => (
                    <td key={d} className={`px-1 py-2 text-center font-mono text-[11px] tabular-nums ${weekendDay(d) ? "bg-neutral-100" : ""} ${dayTotals[d] ? "text-ink font-semibold" : "text-neutral-300"}`}>
                      {dayTotals[d] ? fmtH(dayTotals[d]) : "·"}
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right font-display font-bold text-brand tabular-nums">
                    {grandTotal.toFixed(1)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </PortalLayout>
  );
}
